// Invites by email, under the league's link and code before the draft
// (PLAN-2026-10-28-by-email §3.1, §6, scenario B2): a commissioner types
// addresses, each gets one email with the link, and the ones who have not
// joined yet are listed with a way to take each back.
//
// SAID BEFORE THE SEND: an address is invited to a league once, ever
// (NOTIFICATIONS.md §5), so the line over the button says it, since there is
// no sending one again.
//
// WHAT A SEND DID STAYS ON SCREEN until the next one: who was emailed, and
// beside each address that was not, why, in the server's words. A toast would
// carry the first and lose the rest, and a refusal a commissioner can act on
// ("already in the league", "asked not to get league invites") is the part
// they need to read.

import { useState } from 'react';
import { INVITES_PER_SEND, type EmailInvite, type InvitesSent } from '@fsp/domain';
import { messageOf } from '../../data/api';
import { sendInvites, takeBackInvite } from '../../data/leagueSetup';
import { Button, Callout, Caption, Line, Stack, Text, TextArea, tell } from '../../kit/ui';
import { Confirm } from './Confirm';

/**
 * The addresses in what was typed: split at commas, semicolons, spaces and
 * lines, each once whatever its case, as the server counts them.
 */
export function addressesIn(typed: string): string[] {
  const seen = new Set<string>();
  return typed
    .split(/[\s,;]+/)
    .map((a) => a.trim())
    .filter((a) => {
      const key = a.toLowerCase();
      if (a.length === 0 || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
}

function sentOn(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export function EmailInvites({
  leagueId,
  invites,
  onChanged,
}: {
  leagueId: string;
  /** The pending list, as the league's setup read has it. */
  invites: readonly EmailInvite[];
  /** Read the league again: a send or a take-back moved the list. */
  onChanged: () => void;
}) {
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<InvitesSent | null>(null);
  const addresses = addressesIn(typed);
  const tooMany = addresses.length > INVITES_PER_SEND;

  const send = () => {
    if (addresses.length === 0 || tooMany) return;
    setBusy(true);
    void sendInvites(leagueId, addresses).then((reply) => {
      setBusy(false);
      if (reply.status !== 'ok') {
        tell(
          messageOf(
            reply,
            'The invites did not go.',
            'Could not reach the site. Nothing was sent.',
          ),
          reply.status === 'refused' ? 'warn' : 'bad',
        );
        return;
      }
      setResult(reply.data);
      // WHAT CAN STILL GO STAYS TYPED: a typo to fix, and an email that did
      // not go or was past the day, to send again. The rest is done with.
      const again = new Set(
        reply.data.refused
          .filter((r) => r.reason === 'invalid' || r.reason === 'unsent' || r.reason === 'limit')
          .map((r) => r.address.toLowerCase()),
      );
      setTyped(addresses.filter((a) => again.has(a.toLowerCase())).join(', '));
      if (reply.data.sent.length > 0)
        tell(
          reply.data.sent.length === 1
            ? `Sent to ${reply.data.sent[0]}.`
            : `Sent to ${String(reply.data.sent.length)} people.`,
          'good',
        );
      onChanged();
    });
  };

  const takeBack = (invite: EmailInvite) => {
    setBusy(true);
    void takeBackInvite(leagueId, invite.id).then((reply) => {
      setBusy(false);
      if (reply.status !== 'ok') {
        tell(
          messageOf(reply, 'That invite stands.', 'Could not reach the site. Nothing changed.'),
          'bad',
        );
        return;
      }
      tell(`Took back the invite to ${invite.address}.`, 'good');
      onChanged();
    });
  };

  return (
    <Stack gap={2}>
      <Caption>Or email them</Caption>
      <TextArea
        label="Email addresses"
        placeholder="dave@example.com, sam@example.com"
        value={typed}
        onChange={setTyped}
        maxLength={2000}
        disabled={busy}
      />
      <Text size="xs" tone="secondary" block>
        {tooMany
          ? `Up to ${String(INVITES_PER_SEND)} at a time. Send these in two goes.`
          : 'Each gets one email from the site with the link and the code. An address can be invited to a league once.'}
      </Text>
      <Line>
        <Button
          icon="send"
          variant="secondary"
          onClick={send}
          disabled={busy || addresses.length === 0 || tooMany}
        >
          {addresses.length > 1 ? `Send ${String(addresses.length)} invites` : 'Send invite'}
        </Button>
      </Line>
      {result !== null && result.refused.length > 0 ? (
        <Callout tone="warn">
          <Stack gap={1}>
            {result.sent.length > 0 ? (
              <Text block>
                Sent to {result.sent.length === 1 ? result.sent[0] : `${result.sent.length} people`}
                .
              </Text>
            ) : null}
            {result.refused.map((r) => (
              <Text key={r.address} block>
                <b>{r.address}</b>: {r.message}
              </Text>
            ))}
          </Stack>
        </Callout>
      ) : null}
      {invites.length > 0 ? (
        <Stack gap={1}>
          <Caption>Invited, not in yet</Caption>
          <ul className="fsp-invites">
            {invites.map((invite) => (
              <li key={invite.id} className="fsp-invites-row">
                <div className="fsp-grow">
                  <Text block nowrap>
                    {invite.address}
                  </Text>
                  <Text size="xs" tone="secondary" block>
                    {invite.sentAt === null
                      ? 'Not sent yet. Send it again above.'
                      : `Sent ${sentOn(invite.sentAt)}`}
                  </Text>
                </div>
                <Confirm
                  label="Take back"
                  confirmLabel="Take it back?"
                  tone="warn"
                  busy={busy}
                  onConfirm={() => takeBack(invite)}
                />
              </li>
            ))}
          </ul>
        </Stack>
      ) : null}
    </Stack>
  );
}
